import { useTranslation } from 'react-i18next';

import {
  apexSrc,
  fieldForceManagementSrc,
  multisensoryExperienceSrc,
  networkPlanningSrc,
  pcRoadmapSrc,
  porscheImportAgreementsSrc,
  realEstateSrc,
  retailDataAccessSrc,
  retailDesignSrc,
  strategyActionItemsSrc,
  surveySrc,
} from '@/components';
import { mpiSrc } from '@/components/application-tile';
import { App, APP_FALLBACK, AppLink } from '@/config/app';
import { NAMESPACES } from '@/config/i18n';
import { useAbilityContext } from '@/context';
import { getPpnSlug } from '@/helpers';

export type SingleAppConfig = {
  name: string;
  href: string;
  imgSrc: string;
  hasAccess: boolean;
};

export type ApplicationConfig = Record<
  App | AppLink | typeof APP_FALLBACK,
  SingleAppConfig
>;

export const useApplicationConfigs = (): ApplicationConfig => {
  const { t } = useTranslation(NAMESPACES);
  const { state: ability } = useAbilityContext();
  const ppnSlug = getPpnSlug();

  return {
    [App.FieldForceManagement]: {
      name: t('common:app.ffm'),
      href: '/field-force-management/',
      imgSrc: fieldForceManagementSrc,
      hasAccess: ability.can('access', 'App', 'FieldForceManagement'),
    },
    [App.Greenland]: {
      name: t('common:app.greenland'),
      href: '/retail-data-access/',
      imgSrc: retailDataAccessSrc,
      hasAccess: ability.can('access', 'App', 'Greenland'),
    },
    [App.PorscheImportAgreements]: {
      name: t('common:app.porscheImportAgreements'),
      href: '/porsche-import-agreements/',
      imgSrc: porscheImportAgreementsSrc,
      hasAccess: ability.can('access', 'App', 'PorscheImportAgreements'),
    },
    [App.PorscheStrategy2030]: {
      name: t('common:app.porscheStrategy2030'),
      href: '/strategy-action-items/',
      imgSrc: strategyActionItemsSrc,
      hasAccess: ability.can('access', 'App', 'PorscheStrategy2030'),
    },
    [App.MultisensoryExperience]: {
      name: t('common:app.multisensoryExperience'),
      href: '/multisensory-experience/',
      imgSrc: multisensoryExperienceSrc,
      hasAccess: ability.can('access', 'App', 'MultisensoryExperience'),
    },
    [App.NetworkPlanning]: {
      name: t('common:app.networkPlanning'),
      href: '/network-planning/',
      imgSrc: networkPlanningSrc,
      hasAccess: ability.can('access', 'App', 'NetworkPlanning'),
    },
    [App.PcRoadmap]: {
      name: t('common:app.pcRoadmap'),
      href: '/pc-roadmap/',
      imgSrc: pcRoadmapSrc,
      hasAccess: ability.can('access', 'App', 'PcRoadmap'),
    },
    [App.RetailDesign]: {
      name: t('common:app.retailDesign'),
      href: '/retail-design/',
      imgSrc: retailDesignSrc,
      hasAccess: ability.can('access', 'App', 'RetailDesign'),
    },
    [App.Survey]: {
      name: t('common:app.survey'),
      href: '/survey/',
      imgSrc: surveySrc,
      hasAccess: ability.can('access', 'App', 'Survey'),
    },
    [App.Apex]: {
      name: t('common:app.apex'),
      href: '/apex/',
      imgSrc: apexSrc,
      hasAccess: ability.can('access', 'App', 'Apex'),
    },
    [App.RealEstate]: {
      name: t('common:app.realEstate'),
      href: '/real-estate/',
      imgSrc: realEstateSrc,
      hasAccess: ability.can('access', 'App', 'RealEstate'),
    },
    [AppLink.Mpi]: {
      name: t('common:app.mpi'),
      href: `https://${ppnSlug}.porsche.com/ppnmda/appforward.do?cn=market_potential_intelligence_mpi`,
      imgSrc: mpiSrc,
      hasAccess: ability.can('access', 'App', 'Mpi'),
    },

    // Fallback

    [APP_FALLBACK]: {
      name: t('common:title'),
      href: '/',
      imgSrc: '',
      hasAccess: true,
    },
  } as const;
};
